import { db } from '../storage/db.ts';
import type { ProjectFile } from '../storage/record.ts';
import { normalizeFileUris } from '../storage/record.ts';
import type { BlobJob, ProjectSync, TrashEntry } from './types.ts';

/** Persistence the sync engine needs (Dexie in the app, in-memory in tests). */
export interface SyncStore {
  getCursor(userId: string): Promise<string | null>;
  setCursor(userId: string, cursor: string | null): Promise<void>;
  syncRows(userId: string): Promise<ProjectSync[]>;
  getSync(id: string): Promise<ProjectSync | undefined>;
  putSync(row: ProjectSync): Promise<void>;
  deleteSync(id: string): Promise<void>;
  getProject(id: string): Promise<ProjectFile | undefined>;
  putProject(file: ProjectFile): Promise<void>;
  deleteProject(id: string): Promise<void>;
  jobs(userId: string): Promise<BlobJob[]>;
  putJob(job: BlobJob): Promise<void>;
  deleteJob(key: string): Promise<void>;
  putTrash(entry: TrashEntry): Promise<void>;
  deleteTrash(id: string): Promise<void>;
  putCatalog(orgId: string, catalog: unknown): Promise<void>;
}

export class DexieSyncStore implements SyncStore {
  async getCursor(userId: string) {
    return ((await db().kv.get(`sync.cursor.${userId}`))?.value as string | null | undefined) ?? null;
  }
  async setCursor(userId: string, cursor: string | null) {
    await db().kv.put({ key: `sync.cursor.${userId}`, value: cursor });
  }
  syncRows(userId: string) {
    return db().projectSync.where('userId').equals(userId).toArray();
  }
  getSync(id: string) {
    return db().projectSync.get(id);
  }
  async putSync(row: ProjectSync) {
    await db().projectSync.put(row);
  }
  async deleteSync(id: string) {
    await db().projectSync.delete(id);
  }
  async getProject(id: string) {
    const f = await db().projects.get(id);
    return f ? normalizeFileUris(f) : undefined;
  }
  async putProject(file: ProjectFile) {
    await db().projects.put(file);
  }
  async deleteProject(id: string) {
    await db().projects.delete(id);
  }
  jobs(userId: string) {
    return db().blobJobs.where('userId').equals(userId).toArray();
  }
  async putJob(job: BlobJob) {
    await db().blobJobs.put(job);
  }
  async deleteJob(key: string) {
    await db().blobJobs.delete(key);
  }
  async putTrash(entry: TrashEntry) {
    await db().trash.put(entry);
  }
  async deleteTrash(id: string) {
    await db().trash.delete(id);
  }
  async putCatalog(orgId: string, catalog: unknown) {
    await db().kv.put({ key: `catalog.${orgId}`, value: catalog });
  }
}

/** In-memory store for tests. */
export class MemorySyncStore implements SyncStore {
  cursor = new Map<string, string | null>();
  sync = new Map<string, ProjectSync>();
  projects = new Map<string, ProjectFile>();
  blobJobs = new Map<string, BlobJob>();
  trash = new Map<string, TrashEntry>();
  catalogs = new Map<string, unknown>();
  async getCursor(u: string) {
    return this.cursor.get(u) ?? null;
  }
  async setCursor(u: string, c: string | null) {
    this.cursor.set(u, c);
  }
  async syncRows(u: string) {
    return [...this.sync.values()].filter((r) => r.userId === u).map((r) => ({ ...r }));
  }
  async getSync(id: string) {
    const r = this.sync.get(id);
    return r ? { ...r } : undefined;
  }
  async putSync(r: ProjectSync) {
    this.sync.set(r.id, { ...r });
  }
  async deleteSync(id: string) {
    this.sync.delete(id);
  }
  async getProject(id: string) {
    return this.projects.get(id);
  }
  async putProject(f: ProjectFile) {
    this.projects.set(f.id, f);
  }
  async deleteProject(id: string) {
    this.projects.delete(id);
  }
  async jobs(u: string) {
    return [...this.blobJobs.values()].filter((j) => j.userId === u);
  }
  async putJob(j: BlobJob) {
    this.blobJobs.set(j.key, { ...j });
  }
  async deleteJob(k: string) {
    this.blobJobs.delete(k);
  }
  async putTrash(e: TrashEntry) {
    this.trash.set(e.id, e);
  }
  async deleteTrash(id: string) {
    this.trash.delete(id);
  }
  async putCatalog(orgId: string, c: unknown) {
    this.catalogs.set(orgId, c);
  }
}
